import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../Auth/AuthContext';
import api from '../../utils/api';
import Navigation from '../Navigation/navigation';

const EditWorkExperience = () => {
  const { staffId } = useParams();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [staffData, setStaffData] = useState(null);
  const [workExperience, setWorkExperience] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate('/login');
      return;
    }
    if (staffId) {
      fetchStaffData();
      fetchWorkExperience();
    }
  }, [staffId, isAuthenticated, user, navigate]);

  const fetchStaffData = async () => {
    try {
      const response = await api.get(`/api/staff/${staffId}`);
      setStaffData(response.data);
    } catch (error) {
      console.error('Error fetching staff data:', error);
      setError('Error retrieving staff information.');
    }
  };

  const fetchWorkExperience = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/staff/${staffId}/work-experience`);
      setWorkExperience(response.data || []);
    } catch (error) {
      console.error('Error fetching work experience:', error);
      setError('Error retrieving work experience information.');
    } finally {
      setLoading(false);
    }
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  };

  const addWorkExperience = () => {
    const newExperience = {
      previous_workposition: '',
      previous_organization: '',
      description: '',
      start_working_date: '',
      end_working_date: ''
    };
    setWorkExperience(prev => [...prev, newExperience]);
  };

  const updateWorkExperience = (index, field, value) => {
    setWorkExperience(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeWorkExperience = (index) => {
    setWorkExperience(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      await api.put(`/api/staff/${staffId}/work-experience`, { workExperience });
      setSuccessMessage('Work experience updated successfully!');
      setTimeout(() => {
        navigate(`/edit-staff/${staffId}`);
      }, 2000);
    } catch (error) {
      console.error('Error updating work experience:', error);
      setError(error.response?.data?.message || 'Error updating work experience. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/edit-staff/${staffId}`);
  };

  if (loading && !staffData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-6xl mx-auto p-5 pt-20">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h4 className="text-xl font-semibold text-gray-800">Loading Work Experience...</h4>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="max-w-6xl mx-auto p-5 pt-20">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">Edit Work Experience</h1>
              <p className="text-gray-600">
                Managing work experience for: <strong>{staffData?.fullname}</strong> (ID: {staffData?.staff_id})
              </p>
            </div>
            <button
              onClick={handleCancel}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              ← Back
            </button>
          </div>
        </div>

        {/* Success and Error Messages */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              {successMessage}
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold">Previous Work Experience</h2>
                <p className="text-blue-100 mt-1">Manage previous employment history</p>
              </div>
              <button
                type="button"
                onClick={addWorkExperience}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors"
              >
                Add Work Experience
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {workExperience.length === 0 && (
              <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="mt-2 text-sm text-gray-600">No work experience added yet.</p>
                <p className="text-sm text-gray-500">Click "Add Work Experience" above to get started.</p>
              </div>
            )}

            {workExperience.map((experience, index) => (
              <div key={index} className="p-6 border border-gray-200 rounded-lg bg-white shadow-sm space-y-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Work Experience #{index + 1}</h3>
                  <button
                    type="button"
                    className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 text-sm font-medium transition-colors duration-200"
                    onClick={() => removeWorkExperience(index)}
                  >
                    Remove Experience
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Position <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={experience.previous_workposition}
                      onChange={(e) => updateWorkExperience(index, 'previous_workposition', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                      placeholder="Enter position title"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Organization/Clinic <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={experience.previous_organization}
                      onChange={(e) => updateWorkExperience(index, 'previous_organization', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                      placeholder="Enter organization name"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Start Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formatDateForInput(experience.start_working_date)}
                      onChange={(e) => updateWorkExperience(index, 'start_working_date', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">End Date</label>
                    <input
                      type="date"
                      value={formatDateForInput(experience.end_working_date)}
                      onChange={(e) => updateWorkExperience(index, 'end_working_date', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                    />
                  </div>
                </div>

                {/* Description field */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Job Description
                  </label>
                  <textarea
                    value={experience.description || ''}
                    onChange={(e) => updateWorkExperience(index, 'description', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                    placeholder="Describe your role and responsibilities"
                    rows={3}
                  />
                </div>

                {/* Experience Summary */}
                {experience.previous_workposition && experience.previous_organization && (
                  <div className="mt-4 p-3 bg-gray-100 rounded-lg">
                    <p className="text-sm text-gray-800">
                      <strong>Summary:</strong> {experience.previous_workposition} at {experience.previous_organization}
                      {experience.start_working_date && ` (${new Date(experience.start_working_date).toLocaleDateString()}`}
                      {experience.end_working_date && ` - ${new Date(experience.end_working_date).toLocaleDateString()}`}
                      {experience.start_working_date && !experience.end_working_date && ' - Present'}
                      {experience.start_working_date && ')'}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-4">
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <span className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </span>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditWorkExperience;
